/**
 * ALDER SEARCH — Individual Article Page
 * Full article body with related articles sidebar
 */

import { useEffect } from "react";
import { Link, useParams } from "wouter";
import { insights } from "@/data/insights";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = insights.find((i) => i.slug === slug);

  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content") ?? "";
    const articleUrl = `https://aldersearch.com/insights/${article.slug}`;
    const metaDescription = article.metaDescription;

    // Page title & description
    document.title = `${article.title} | Alder Search`;
    if (metaDesc) metaDesc.setAttribute("content", metaDescription);

    // OG tags — update existing or create new
    const setMeta = (property: string, content: string, attr = "property") => {
      let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, property);
        el.setAttribute("data-article-meta", "true");
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const ogImage = "https://d2xsxph8kpxj0f.cloudfront.net/310519663529055054/oKC3ZUHFZn4f6dythu4jrQ/favicon_0d692b59.svg";

    setMeta("og:title", `${article.title} | Alder Search`);
    setMeta("og:description", metaDescription);
    setMeta("og:type", "article");
    setMeta("og:url", articleUrl);
    setMeta("og:image", ogImage);
    setMeta("twitter:title", `${article.title} | Alder Search`);
    setMeta("twitter:description", metaDescription);
    setMeta("twitter:image", ogImage, "name");

    // ArticlePosting JSON-LD
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "article-jsonld";
    script.appendChild(document.createTextNode(JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: metaDescription,
      author: {
        "@type": "Person",
        name: "Ryan Ollerenshaw",
        url: "https://www.linkedin.com/in/ryanollerenshaw/",
        sameAs: ["https://www.linkedin.com/in/ryanollerenshaw/"],
        jobTitle: "Founding Partner",
        description: "Ryan Ollerenshaw is the Founding Partner of Alder Search, a retained executive search firm exclusively focused on VC and PE investors and their portfolio companies.",
        knowsAbout: [
          "VC and PE executive search",
          "retained executive search",
          "CEO recruitment for VC- and PE-backed companies",
          "leadership assessment for VC and PE",
          "value creation through talent",
          "C-suite placement in VC- and PE-backed businesses",
          "management team due diligence",
        ],
        worksFor: {
          "@type": "Organization",
          name: "Alder Search",
          url: "https://aldersearch.com",
          sameAs: ["https://aldersearch.com"],
        },
      },
      publisher: {
        "@type": "Organization",
        name: "Alder Search",
        url: "https://aldersearch.com",
        sameAs: ["https://aldersearch.com"],
        description: "Alder Search is a retained executive search firm exclusively focused on VC and PE investors and their portfolio companies. We place C-suite and board leaders who drive value creation and build for exit.",
        logo: {
          "@type": "ImageObject",
          url: "https://aldersearch.com/favicon.svg",
        },
      },
      datePublished: article.date,
      dateModified: article.date,
      url: articleUrl,
      mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
      image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663529055054/oKC3ZUHFZn4f6dythu4jrQ/alder-og-image-26YXP9YAMcsr3VJhDisEbg.png",
    })));
    document.head.appendChild(script);
    // BreadcrumbList JSON-LD
    const breadcrumb = document.createElement("script");
    breadcrumb.type = "application/ld+json";
    breadcrumb.id = "article-breadcrumb-jsonld";
    breadcrumb.appendChild(document.createTextNode(JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://aldersearch.com/" },
        { "@type": "ListItem", "position": 2, "name": "Insights", "item": "https://aldersearch.com/insights" },
        { "@type": "ListItem", "position": 3, "name": article.title, "item": articleUrl }
      ]
    })));
    document.head.appendChild(breadcrumb);

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute("content", prevDesc);
      // Restore OG tags to homepage defaults
      setMeta("og:title", "Alder Search \u2014 VC & PE-Focused Executive Search");
      setMeta("og:description", "Retained executive search for VC and PE. We place leaders and senior practitioners who drive your returns.");
      setMeta("og:type", "website");
      setMeta("og:url", "https://aldersearch.com");
      setMeta("twitter:title", "Alder Search \u2014 VC & PE-Focused Executive Search");
      setMeta("twitter:description", "Retained executive search for VC and PE. We place leaders and senior practitioners who drive your returns.");
      // Remove injected JSON-LD
      document.getElementById("article-jsonld")?.remove();
      document.getElementById("article-breadcrumb-jsonld")?.remove();
      // Remove any dynamically created meta tags
      document.querySelectorAll('meta[data-article-meta="true"]').forEach(el => el.remove());
    };
  }, [article]);

  if (!article) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-6"
        style={{ background: "oklch(0.97 0.008 80)" }}
      >
        <h1
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontWeight: 700,
            fontSize: "2rem",
            color: "oklch(0.22 0.06 155)",
          }}
        >
          Article not found
        </h1>
        <Link
          href="/insights"
          style={{
            fontFamily: "'DM Sans', system-ui, sans-serif",
            fontSize: "0.82rem",
            color: "oklch(0.38 0.07 155)",
            textDecoration: "none",
          }}
        >
          Back to Insights
        </Link>
      </div>
    );
  }

  // Related: same category first, fill remainder with recency, always 3 max
  const sameCategory = insights.filter(
    (i) => i.slug !== slug && i.category === article.category
  );
  const otherRecent = insights.filter(
    (i) => i.slug !== slug && i.category !== article.category
  );
  const related = [...sameCategory, ...otherRecent].slice(0, 3);

  // Split body into blocks — lines starting with "## " become H2 headings
  const blocks = article.body
    .split("\n\n")
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.97 0.008 80)" }}>
      {/* Nav */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-8 py-5"
        style={{
          background: "oklch(0.97 0.008 80 / 0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid oklch(0.88 0.01 80)",
        }}
      >
        <Link
          href="/"
          className="flex items-center gap-3"
          style={{ textDecoration: "none" }}
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: "oklch(0.28 0.07 155)" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C8 2 5 6 5 10c0 5 7 12 7 12s7-7 7-12c0-4-3-8-7-8z"
                fill="oklch(0.72 0.09 75)"
              />
            </svg>
          </div>
          <span
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "1.05rem",
              color: "oklch(0.22 0.06 155)",
              letterSpacing: "-0.01em",
            }}
          >
            Alder Search
          </span>
        </Link>
        <Link
          href="/#contact"
          className="px-5 py-2.5 text-xs font-semibold tracking-widest uppercase"
          style={{
            fontFamily: "'DM Sans', system-ui, sans-serif",
            background: "oklch(0.28 0.07 155)",
            color: "oklch(0.92 0.01 80)",
            borderRadius: "2px",
            textDecoration: "none",
            letterSpacing: "0.1em",
          }}
        >
          Engage Us
        </Link>
      </nav>

      {/* Article header */}
      <div
        className="pt-36 pb-16 px-8"
        style={{ background: "oklch(0.22 0.065 155)" }}
      >
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mb-8">
            <Link
              href="/insights"
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "0.62rem",
                color: "oklch(0.55 0.02 155)",
                letterSpacing: "0.08em",
                textDecoration: "none",
                textTransform: "uppercase",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color =
                  "oklch(0.72 0.09 75)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color =
                  "oklch(0.55 0.02 155)";
              }}
            >
              Insights
            </Link>
            <span style={{ color: "oklch(0.38 0.07 155)", fontSize: "0.7rem" }}>
              /
            </span>
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "0.62rem",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {article.category}
            </span>
          </div>

          <h1
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(1.8rem, 4vw, 3rem)",
              color: "oklch(0.952 0.012 80)",
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              marginBottom: "1.5rem",
            }}
          >
            {article.title}
          </h1>

          <div className="flex items-center gap-4">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
              style={{
                background: "oklch(0.62 0.085 75 / 0.2)",
                color: "oklch(0.72 0.09 75)",
                fontFamily: "'DM Sans', system-ui, sans-serif",
              }}
            >
              RO
            </div>
            <div>
              <p
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  fontSize: "0.78rem",
                  fontWeight: 500,
                  color: "oklch(0.82 0.015 80)",
                  lineHeight: 1.3,
                }}
              >
                Ryan Ollerenshaw
              </p>
              <p
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "0.6rem",
                  color: "oklch(0.55 0.02 155)",
                  letterSpacing: "0.06em",
                }}
              >
                Founding Partner, Alder Search
              </p>
            </div>
            <span style={{ color: "oklch(0.38 0.07 155)", marginLeft: "8px" }}>
              ·
            </span>
            <span
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "0.62rem",
                color: "oklch(0.55 0.02 155)",
                letterSpacing: "0.06em",
              }}
            >
              {article.date}
            </span>
          </div>
        </div>
      </div>
      {/* Article body */}
      <div className="px-8 py-16">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Main content */}
          <div className="lg:col-span-2">
            {blocks.map((block, i) => {
              if (block.startsWith("## ")) {
                const headingText = block.slice(3);
                return (
                  <h2
                    key={i}
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontWeight: 700,
                      fontSize: "clamp(1.15rem, 2.5vw, 1.45rem)",
                      color: "oklch(0.22 0.06 155)",
                      lineHeight: 1.3,
                      letterSpacing: "-0.015em",
                      marginTop: "2.4rem",
                      marginBottom: "1rem",
                    }}
                  >
                    {headingText}
                  </h2>
                );
              }
              return (
                <p
                  key={i}
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontWeight: 300,
                    fontSize: "1.05rem",
                    lineHeight: 1.85,
                    color: "oklch(0.28 0.04 80)",
                    marginBottom: "1.6rem",
                  }}
                >
                  {block}
                </p>
              );
            })}

            {/* CTA */}
            <div
              className="mt-12 p-8"
              style={{
                background: "oklch(0.22 0.065 155)",
                borderRadius: "4px",
                borderLeft: "3px solid oklch(0.62 0.085 75)",
              }}
            >
              <p
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 600,
                  fontSize: "1.1rem",
                  color: "oklch(0.952 0.012 80)",
                  marginBottom: "0.6rem",
                  fontStyle: "italic",
                }}
              >
                Ready to discuss a leadership mandate?
              </p>
              <p
                style={{
                  fontFamily: "'Source Serif 4', Georgia, serif",
                  fontWeight: 300,
                  fontSize: "0.88rem",
                  lineHeight: 1.7,
                  color: "oklch(0.72 0.02 155)",
                  marginBottom: "1.2rem",
                }}
              >
                Alder Search works exclusively with VC and PE investors and their
                portfolio companies. All conversations are treated with strict
                confidentiality.
              </p>
              <Link
                href="/#contact"
                className="inline-block px-6 py-3 text-xs font-semibold tracking-widest uppercase"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  background: "oklch(0.62 0.085 75)",
                  color: "oklch(0.18 0.055 155)",
                  borderRadius: "2px",
                  textDecoration: "none",
                  letterSpacing: "0.1em",
                }}
              >
                Start a Conversation
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-28">
              {related.length > 0 && (
                <div>
                  <h3
                    style={{
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      fontWeight: 600,
                      fontSize: "0.7rem",
                      color: "oklch(0.45 0.02 155)",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      marginBottom: "1rem",
                    }}
                  >
                    Related Articles
                  </h3>
                  <div className="flex flex-col gap-4">
                    {related.map((rel) => (
                      <Link
                        key={rel.slug}
                        href={`/insights/${rel.slug}`}
                        className="block group"
                        style={{ textDecoration: "none" }}
                      >
                        <div
                          className="p-4 transition-all duration-200"
                          style={{
                            background: "white",
                            borderRadius: "3px",
                            border: "1px solid oklch(0.88 0.01 80)",
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLDivElement).style.borderColor =
                              "oklch(0.28 0.07 155 / 0.35)";
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLDivElement).style.borderColor =
                              "oklch(0.88 0.01 80)";
                          }}
                        >
                          <p
                            className="group-hover:opacity-75 transition-opacity"
                            style={{
                              fontFamily: "'Playfair Display', Georgia, serif",
                              fontWeight: 600,
                              fontSize: "0.88rem",
                              color: "oklch(0.22 0.06 155)",
                              lineHeight: 1.35,
                              marginBottom: "0.4rem",
                            }}
                          >
                            {rel.title}
                          </p>
                          <span
                            style={{
                              fontFamily: "'DM Mono', monospace",
                              fontSize: "0.58rem",
                              color: "oklch(0.6 0.015 80)",
                              letterSpacing: "0.06em",
                            }}
                          >
                            {rel.date}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div
                className="mt-8 p-5"
                style={{
                  background: "oklch(0.28 0.07 155)",
                  borderRadius: "3px",
                }}
              >
                <p
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    color: "oklch(0.952 0.012 80)",
                    marginBottom: "0.5rem",
                    fontStyle: "italic",
                  }}
                >
                  Have an active mandate?
                </p>
                <p
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontWeight: 300,
                    fontSize: "0.78rem",
                    lineHeight: 1.65,
                    color: "oklch(0.72 0.02 155)",
                    marginBottom: "1rem",
                  }}
                >
                  Speak with Ryan directly about your search requirements.
                </p>
                <a
                  href="mailto:engage@aldersearch.com"
                  style={{
                    fontFamily: "'DM Sans', system-ui, sans-serif",
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "oklch(0.72 0.09 75)",
                    textDecoration: "none",
                  }}
                >
                  engage@aldersearch.com →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related Articles — full-width bottom section */}
      {related.length > 0 && (
        <div
          className="px-8 py-16"
          style={{ borderTop: "1px solid oklch(0.88 0.01 80)", background: "oklch(0.97 0.008 80)" }}
        >
          <div className="max-w-5xl mx-auto">
            <p
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 600,
                fontSize: "0.7rem",
                color: "oklch(0.45 0.02 155)",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                marginBottom: "0.75rem",
              }}
            >
              Related Insights
            </p>
            <h2
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: "1.6rem",
                color: "oklch(0.22 0.06 155)",
                marginBottom: "2.5rem",
                lineHeight: 1.2,
              }}
            >
              Continue Reading
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((rel) => (
                <Link
                  key={rel.slug}
                  href={`/insights/${rel.slug}`}
                  className="block group"
                  style={{ textDecoration: "none" }}
                >
                  <div
                    className="h-full flex flex-col p-6 transition-all duration-200"
                    style={{
                      background: "white",
                      borderRadius: "3px",
                      border: "1px solid oklch(0.88 0.01 80)",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.borderColor =
                        "oklch(0.72 0.09 75)";
                      (e.currentTarget as HTMLDivElement).style.boxShadow =
                        "0 4px 20px oklch(0.72 0.09 75 / 0.12)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.borderColor =
                        "oklch(0.88 0.01 80)";
                      (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
                    }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span
                        style={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: "0.6rem",
                          fontWeight: 500,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          color: "oklch(0.72 0.09 75)",
                          background: "oklch(0.72 0.09 75 / 0.1)",
                          padding: "0.2rem 0.55rem",
                          borderRadius: "2px",
                        }}
                      >
                        {rel.category}
                      </span>
                      <span
                        style={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: "0.6rem",
                          color: "oklch(0.6 0.015 80)",
                          letterSpacing: "0.06em",
                        }}
                      >
                        {rel.date}
                      </span>
                    </div>
                    <h3
                      className="group-hover:opacity-80 transition-opacity flex-1"
                      style={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: "oklch(0.22 0.06 155)",
                        lineHeight: 1.35,
                        marginBottom: "0.75rem",
                      }}
                    >
                      {rel.title}
                    </h3>
                    <p
                      style={{
                        fontFamily: "'Source Serif 4', Georgia, serif",
                        fontWeight: 300,
                        fontSize: "0.82rem",
                        lineHeight: 1.65,
                        color: "oklch(0.42 0.025 155)",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {rel.excerpt}
                    </p>
                    <p
                      className="mt-4"
                      style={{
                        fontFamily: "'DM Sans', system-ui, sans-serif",
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "oklch(0.72 0.09 75)",
                      }}
                    >
                      Read more →
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Back link */}
      <div
        className="px-8 py-8"
        style={{ borderTop: "1px solid oklch(0.88 0.01 80)" }}
      >
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link
            href="/insights"
            style={{
              fontFamily: "'DM Sans', system-ui, sans-serif",
              fontSize: "0.78rem",
              color: "oklch(0.38 0.07 155)",
              textDecoration: "none",
              letterSpacing: "0.04em",
            }}
          >
            Back to all insights
          </Link>
          <p
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "0.58rem",
              color: "oklch(0.6 0.015 80)",
              letterSpacing: "0.06em",
            }}
          >
            &copy; {new Date().getFullYear()} Alder Search Ltd.
          </p>
        </div>
      </div>
    </div>
  );
}
